import { useState, useEffect } from 'react';
import { getApiUrl } from '../lib/api';

interface AdminProps {
  session: { adminKey: string };
}

interface Rating {
  id: number;
  roller_name: string;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
}

interface TourismDestination {
  id: number;
  country: string;
  city: string;
  last_updated?: string;
  data_source?: string;
  flag_count?: number;
  needs_review?: boolean;
  tourist_legal_status?: string;
  can_tourists_buy?: string;
  min_age?: number;
  where_to_buy?: string;
  possession_limit?: string;
  consumption_rules?: string;
  avg_price_gram?: string;
  popular_strains?: string;
  top_spots?: string;
  cultural_tips?: string;
  warnings?: string;
  best_season?: string;
  vibe?: string;
  description?: string;
}

interface Flag {
  id: number;
  reason: string;
  user_note?: string;
  ip?: string;
  created_at?: string;
}

export default function Admin({ session }: AdminProps) {
  const [tab, setTab] = useState<'rollers' | 'ratings' | 'destinations'>('rollers');
  const [rollers, setRollers] = useState<string[]>([]);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [newRoller, setNewRoller] = useState('');

  // Destinations tab state
  const [destinations, setDestinations] = useState<TourismDestination[]>([]);
  const [destPage, setDestPage] = useState(1);
  const [destTotal, setDestTotal] = useState(0);
  const [destFilter, setDestFilter] = useState<'all' | 'flagged' | 'stale'>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<TourismDestination>>({});
  const [destFlags, setDestFlags] = useState<Flag[]>([]);
  const [showFlags, setShowFlags] = useState(false);

  const headers = { 'x-admin-key': session.adminKey };

  const loadData = async () => {
    const [r1, r2] = await Promise.all([
      fetch(getApiUrl('/api/rollers')).then(r => r.json()),
      fetch(getApiUrl('/api/admin/ratings'), { headers }).then(r => r.json()),
    ]);
    setRollers(r1);
    if (Array.isArray(r2)) setRatings(r2);
  };

  const loadDestinations = async () => {
    try {
      const response = await fetch(
        `/api/admin/tourism?page=${destPage}&filter=${destFilter}`,
        { headers }
      );
      const data = await response.json();
      setDestinations(data.items || []);
      setDestTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading destinations:', err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadDestinations();
  }, [destPage, destFilter]);

  const addRoller = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoller.trim()) return;
    await fetch(getApiUrl('/api/admin/rollers'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newRoller.trim(), adminKey: session.adminKey }),
    });
    setNewRoller('');
    loadData();
  };

  const removeRoller = async (name: string) => {
    if (!confirm(`Remove roller "${name}"?`)) return;
    await fetch(`/api/admin/rollers/${encodeURIComponent(name)}`, { method: 'DELETE', headers });
    loadData();
  };

  const deleteRating = async (id: number) => {
    if (!confirm('Delete this rating?')) return;
    await fetch(`/api/admin/ratings/${id}`, { method: 'DELETE', headers });
    loadData();
  };

  const startEditingDestination = async (dest: TourismDestination) => {
    setEditingId(dest.id);
    setEditForm(dest);
    try {
      const response = await fetch(`/api/admin/tourism/${dest.id}`, { headers });
      const data = await response.json();
      if (data.flags) setDestFlags(data.flags);
    } catch (err) {
      console.error('Error loading destination details:', err);
    }
  };

  const saveDestination = async () => {
    if (!editingId) return;
    try {
      const payload = {
        ...editForm,
        popular_strains: typeof editForm.popular_strains === 'string'
          ? editForm.popular_strains.split(',').map(s => s.trim()).filter(Boolean)
          : editForm.popular_strains,
        top_spots: typeof editForm.top_spots === 'string'
          ? editForm.top_spots.split(',').map(s => s.trim()).filter(Boolean)
          : editForm.top_spots,
      };

      const response = await fetch(`/api/admin/tourism/${editingId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setEditingId(null);
        setEditForm({});
        loadDestinations();
      }
    } catch (err) {
      console.error('Error saving destination:', err);
    }
  };

  const clearFlags = async () => {
    if (!editingId || !confirm('Clear all flags for this destination?')) return;
    try {
      const response = await fetch(`/api/admin/tourism/${editingId}/clear-flags`, {
        method: 'POST',
        headers,
      });
      if (response.ok) {
        setDestFlags([]);
        loadDestinations();
      }
    } catch (err) {
      console.error('Error clearing flags:', err);
    }
  };

  function freshnessBadge(lastUpdated?: string) {
    if (!lastUpdated) return { label: '◦ Unknown', className: 'bg-white/10 text-white/60' };
    const days = Math.floor((Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60 * 60 * 24));
    if (days < 90) return { label: '● Fresh', className: 'bg-green-500/20 text-green-300' };
    if (days < 180) return { label: '◐ Aging', className: 'bg-amber-500/20 text-amber-300' };
    return { label: '◯ Stale', className: 'bg-red-500/20 text-red-300' };
  }

  const badge = freshnessBadge(editForm.last_updated as string);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <h2 className="text-2xl font-bold text-cream">Admin Panel</h2>

      {/* Tab Bar */}
      <div className="flex gap-2 border-b border-cannabis-700">
        {(['rollers', 'ratings', 'destinations'] as const).map(t => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              if (t === 'destinations') setDestPage(1);
            }}
            className={`px-4 py-2 text-sm font-semibold transition ${
              tab === t
                ? 'text-cannabis-400 border-b-2 border-cannabis-400'
                : 'text-cream/50 hover:text-cream/70'
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Rollers Tab */}
      {tab === 'rollers' && (
        <section className="bg-cannabis-800 rounded-xl p-5 border border-cannabis-700">
          <h3 className="text-lg font-semibold text-cream mb-4">Manage Rollers</h3>
          <form onSubmit={addRoller} className="flex gap-2 mb-4">
            <input
              value={newRoller}
              onChange={e => setNewRoller(e.target.value)}
              placeholder="New roller name"
              className="flex-1 bg-cannabis-900 border border-cannabis-600 rounded-lg px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
            />
            <button type="submit" className="bg-cannabis-600 hover:bg-cannabis-500 text-cream px-4 py-2 rounded-lg text-sm transition">
              Add
            </button>
          </form>
          <div className="flex flex-wrap gap-2">
            {rollers.map(name => (
              <span key={name} className="inline-flex items-center gap-1 bg-cannabis-900 text-cream/80 text-sm px-3 py-1 rounded-full">
                {name}
                <button onClick={() => removeRoller(name)} className="text-red-400 hover:text-red-300 ml-1">&times;</button>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Ratings Tab */}
      {tab === 'ratings' && (
        <section className="bg-cannabis-800 rounded-xl p-5 border border-cannabis-700">
          <h3 className="text-lg font-semibold text-cream mb-4">All Ratings ({ratings.length})</h3>
          {ratings.length === 0 ? (
            <p className="text-cream/40 text-center py-4">No ratings yet.</p>
          ) : (
            <div className="space-y-3">
              {ratings.map(r => (
                <div key={r.id} className="flex items-start gap-3 bg-cannabis-900/50 rounded-lg p-3">
                  {r.image_path && (
                    <img src={r.image_path} alt="" className="w-16 h-16 object-cover rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-cream font-semibold text-sm">{r.roller_name}</span>
                      <span className="text-cream/30 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-cannabis-400 font-mono text-sm">{r.final_score?.toFixed(2)}</div>
                    <p className="text-cream/50 italic text-xs mt-1 line-clamp-2">"{r.verdict}"</p>
                  </div>
                  <button
                    onClick={() => deleteRating(r.id)}
                    className="text-red-400 hover:text-red-300 text-sm shrink-0"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Destinations Tab */}
      {tab === 'destinations' && (
        <div className="space-y-4">
          {/* Filter Pills */}
          <div className="flex gap-2">
            {(['all', 'flagged', 'stale'] as const).map(f => (
              <button
                key={f}
                onClick={() => {
                  setDestFilter(f);
                  setDestPage(1);
                }}
                className={`px-3 py-1 text-sm font-semibold rounded-full transition ${
                  destFilter === f
                    ? 'bg-cannabis-600 text-cream'
                    : 'bg-cannabis-900 text-cream/60 hover:text-cream'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Table */}
          {destinations.length === 0 ? (
            <p className="text-cream/40 text-center py-8">No destinations found.</p>
          ) : (
            <div className="bg-cannabis-800 rounded-xl border border-cannabis-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-cannabis-700 bg-cannabis-900/50">
                      <th className="px-4 py-3 text-left font-semibold text-cream">Country</th>
                      <th className="px-4 py-3 text-left font-semibold text-cream">City</th>
                      <th className="px-4 py-3 text-left font-semibold text-cream">Last Updated</th>
                      <th className="px-4 py-3 text-center font-semibold text-cream">Flags</th>
                      <th className="px-4 py-3 text-center font-semibold text-cream">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinations.map(dest => (
                      <tr
                        key={dest.id}
                        onClick={() => startEditingDestination(dest)}
                        className="border-b border-cannabis-700/50 hover:bg-cannabis-900/50 cursor-pointer transition"
                      >
                        <td className="px-4 py-3 text-cream">{dest.country}</td>
                        <td className="px-4 py-3 text-cream">{dest.city}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${freshnessBadge(dest.last_updated).className}`}>
                            {freshnessBadge(dest.last_updated).label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {dest.flag_count ? (
                            <span className="inline-block bg-red-600/20 text-red-300 px-2 py-1 rounded-full text-xs font-semibold">
                              {dest.flag_count}
                            </span>
                          ) : (
                            <span className="text-cream/30">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {dest.needs_review ? (
                            <span className="inline-block w-3 h-3 rounded-full bg-amber-500"></span>
                          ) : (
                            <span className="text-cream/30">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {destTotal > 0 && (
            <div className="flex items-center justify-between text-sm text-cream/60">
              <span>Page {destPage} of {Math.ceil(destTotal / 20)}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setDestPage(p => Math.max(1, p - 1))}
                  disabled={destPage === 1}
                  className="px-3 py-1 bg-cannabis-700 rounded hover:bg-cannabis-600 disabled:opacity-50 transition"
                >
                  ← Previous
                </button>
                <button
                  onClick={() => setDestPage(p => (p < Math.ceil(destTotal / 20) ? p + 1 : p))}
                  disabled={destPage >= Math.ceil(destTotal / 20)}
                  className="px-3 py-1 bg-cannabis-700 rounded hover:bg-cannabis-600 disabled:opacity-50 transition"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Edit Form */}
          {editingId !== null && (
            <div className="bg-cannabis-800 rounded-xl p-5 border border-cannabis-700 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-cream">
                  {editForm.country} — {editForm.city}
                </h4>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
                  {badge.label}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Tourist Legal Status"
                  value={editForm.tourist_legal_status || ''}
                  onChange={e => setEditForm({ ...editForm, tourist_legal_status: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Can Tourists Buy?"
                  value={editForm.can_tourists_buy || ''}
                  onChange={e => setEditForm({ ...editForm, can_tourists_buy: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="number"
                  placeholder="Min Age"
                  value={editForm.min_age || ''}
                  onChange={e => setEditForm({ ...editForm, min_age: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Where to Buy"
                  value={editForm.where_to_buy || ''}
                  onChange={e => setEditForm({ ...editForm, where_to_buy: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Possession Limit"
                  value={editForm.possession_limit || ''}
                  onChange={e => setEditForm({ ...editForm, possession_limit: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Avg Price per Gram"
                  value={editForm.avg_price_gram || ''}
                  onChange={e => setEditForm({ ...editForm, avg_price_gram: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Best Season"
                  value={editForm.best_season || ''}
                  onChange={e => setEditForm({ ...editForm, best_season: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
                <input
                  type="text"
                  placeholder="Vibe"
                  value={editForm.vibe || ''}
                  onChange={e => setEditForm({ ...editForm, vibe: e.target.value })}
                  className="bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                />
              </div>

              <textarea
                placeholder="Consumption Rules"
                value={editForm.consumption_rules || ''}
                onChange={e => setEditForm({ ...editForm, consumption_rules: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={2}
              />

              <textarea
                placeholder="Popular Strains (comma-separated)"
                value={Array.isArray(editForm.popular_strains) ? editForm.popular_strains.join(', ') : editForm.popular_strains || ''}
                onChange={e => setEditForm({ ...editForm, popular_strains: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={2}
              />

              <textarea
                placeholder="Top Spots (comma-separated)"
                value={Array.isArray(editForm.top_spots) ? editForm.top_spots.join(', ') : editForm.top_spots || ''}
                onChange={e => setEditForm({ ...editForm, top_spots: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={2}
              />

              <textarea
                placeholder="Cultural Tips"
                value={editForm.cultural_tips || ''}
                onChange={e => setEditForm({ ...editForm, cultural_tips: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={2}
              />

              <textarea
                placeholder="Warnings"
                value={editForm.warnings || ''}
                onChange={e => setEditForm({ ...editForm, warnings: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={2}
              />

              <textarea
                placeholder="Description"
                value={editForm.description || ''}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded px-3 py-2 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-cannabis-400"
                rows={3}
              />

              {/* Pending Reports */}
              {destFlags.length > 0 && (
                <div className="border-t border-cannabis-700 pt-4 space-y-3">
                  <button
                    onClick={() => setShowFlags(!showFlags)}
                    className="text-cream/60 hover:text-cream text-sm font-semibold transition"
                  >
                    {showFlags ? '▼' : '▶'} Pending Reports ({destFlags.length})
                  </button>
                  {showFlags && (
                    <div className="bg-cannabis-900/50 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-cannabis-600 bg-cannabis-900">
                            <th className="px-3 py-2 text-left text-cream/60">Reason</th>
                            <th className="px-3 py-2 text-left text-cream/60">Note</th>
                            <th className="px-3 py-2 text-left text-cream/60">IP</th>
                            <th className="px-3 py-2 text-left text-cream/60">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {destFlags.map(f => (
                            <tr key={f.id} className="border-b border-cannabis-600/30">
                              <td className="px-3 py-2 text-cream/80">{f.reason}</td>
                              <td className="px-3 py-2 text-cream/60 max-w-xs truncate">{f.user_note || '—'}</td>
                              <td className="px-3 py-2 text-cream/40">{f.ip?.split('.').slice(0, 3).join('.')}.*</td>
                              <td className="px-3 py-2 text-cream/40">{f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <button
                    onClick={clearFlags}
                    className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 py-2 rounded text-sm font-semibold transition"
                  >
                    Clear All Flags
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-cannabis-700">
                <button
                  onClick={saveDestination}
                  className="flex-1 bg-cannabis-600 hover:bg-cannabis-500 text-cream py-2 rounded font-semibold transition"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingId(null);
                    setEditForm({});
                    setDestFlags([]);
                  }}
                  className="flex-1 bg-cannabis-900 hover:bg-cannabis-800 text-cream py-2 rounded font-semibold transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
